import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useEmployeesState } from "atoms/employeesAtom";

import { BaseLayout } from "components/BaseLayout/BaseLayout";
import { ModalHeader } from "components/Modal/AppModal";
import { Button } from "components/Button/Button";
import { NamedInput } from "components/NamedInput/NamedInput";
import RadioButton from "components/Radio/Radio";
import { MediumText } from "components/MediumText/MediumText";

import styles from "./styles.module.css";
import { AccessList } from "./AccessList";
import { ModalFooter } from "./ModalFooter";
import { getEmployeesAccess, useAssignAccess, useDeleteAccess } from "./api";

export type FormEmployeesGroup = {
  name: string;
  code: string;
  access: AccessItem[];
  checkedAccess: string[];
};

type Props = {
  isEdit?: boolean;
};

export type AccessItem = {
  accessid: string;
  foroffice: number;
  name: string;
};

const ACCESS_RIGHT = [
  { id: 0, text: "FloraOffice" },
  { id: 1, text: "FloraPoint" },
];

const defaultValues: FormEmployeesGroup = {
  name: "",
  code: "",
  checkedAccess: [],
  access: [],
};

export const EmployeesGroupModal = ({ isEdit }: Props): JSX.Element => {
  const { employeesState, setEmployeesState } = useEmployeesState();
  const [forOffice, setForOffice] = useState(true);

  const { handleSubmit, control, reset, watch, setValue } =
    useForm<FormEmployeesGroup>({
      defaultValues: {
        name: "",
        code: "",
        access: [],
        checkedAccess: [],
      },
    });

  const accessList = watch("access");
  const checkedAccessList = watch("checkedAccess");

  const handleChangeAvailable = (accessid: string) => {
    const isAvailable = checkedAccessList.includes(accessid);

    if (isAvailable) {
      setValue(
        "checkedAccess",
        checkedAccessList.filter((item) => item !== accessid)
      );
    } else {
      setValue("checkedAccess", [...checkedAccessList, accessid]);
    }
  };

  const handleCheckAll = async () => {
    setValue("checkedAccess", [...accessList.map((i) => i.accessid)]);
  };

  const handleUncheckAll = async () => {
    setValue("checkedAccess", []);
  };

  useEffect(() => {
    (async () => {
      const access: AccessItem[] = await getEmployeesAccess({ forOffice });
      if (access?.length) {
        setValue("access", access);
      }
    })();
  }, [forOffice]);

  useEffect(() => {
    if (employeesState.activeGroupItem && isEdit) {
      reset({
        name: employeesState.activeGroupItem.item.name,
        code: employeesState.activeGroupItem.item.usersgroupid,
        access: [],
        checkedAccess: employeesState.activeGroupItem.access.map(
          (i) => i.accessid
        ),
      });
    } else {
      reset(defaultValues);
    }
  }, [employeesState, isEdit]);

  return (
    <BaseLayout
      className={styles.settingsModal}
      header={<ModalHeader title={watch("name") || "Новая группа"} />}
      footer={<ModalFooter handleSubmit={handleSubmit} isEdit={isEdit} />}
    >
      <div>
        <div className="flex mt-4">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <NamedInput
                {...field}
                className="mr-5 h-10 w-72"
                label="Название"
                placeholder="Менеджер по клинингу"
              />
            )}
          />
          {isEdit && (
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <NamedInput
                  {...field}
                  className="h-10 w-12"
                  label="Код"
                  placeholder="123"
                  disabled
                />
              )}
            />
          )}
        </div>
        <div className="flex mt-6">
          <MediumText className="mr-4">Права доступа:</MediumText>
          {ACCESS_RIGHT.map((accessRightItem: any) => (
            <div className="mr-4" key={accessRightItem.id}>
              <RadioButton
                id={accessRightItem.id}
                text={accessRightItem.text}
                name={"usercalctype"}
                onChange={() => {
                  setForOffice(accessRightItem.id === 0);
                }}
                checked={accessRightItem.id === 0 ? forOffice : !forOffice}
                value={accessRightItem.id}
              />
            </div>
          ))}
        </div>
        <div className="flex mt-4">
          <Button className="mr-2" onClick={handleCheckAll}>
            Установить все права
          </Button>
          <Button onClick={handleUncheckAll}>Снять все права</Button>
        </div>
      </div>

      <AccessList
        accessList={accessList}
        checkedAccessList={checkedAccessList}
        onChange={handleChangeAvailable}
      />
    </BaseLayout>
  );
};
